import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { auth, db } from "./firebaseClient";

export const ACTIVE_RIDE_STATUSES = [
  "assigned",
  "enrouteToPickup",
  "atPickupLocation",
  "enrouteToDropoff",
  "driver_enroute",
  "driver_arrived",
  "trip_started",
];

export const DASHBOARD_RIDE_STATUSES = [
  "shared",
  "accepted",
  ...ACTIVE_RIDE_STATUSES,
  "completed",
];

export function toDateAny(value) {
  if (!value) return null;
  if (typeof value?.toDate === "function") return value.toDate();
  if (value instanceof Date) return value;
  const seconds = value?.seconds || value?._seconds;
  if (seconds) return new Date(seconds * 1000);
  if (typeof value === "string" || typeof value === "number") {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatDateTime(value) {
  const d = toDateAny(value);
  if (!d) return "—";
  return d.toLocaleString([], {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function statusLabel(status) {
  const labels = {
    shared: "New Offer",
    accepted: "Accepted",
    assigned: "Assigned",
    enrouteToPickup: "En Route to Pickup",
    atPickupLocation: "At Pickup",
    enrouteToDropoff: "Passenger On Board",
    completed: "Completed",
    cancelled: "Cancelled",
    guestNoShow: "Guest No Show",
    driver_enroute: "Driver En Route",
    driver_arrived: "Driver Arrived",
    trip_started: "Trip Started",
  };
  return labels[status] || String(status || "Unknown").replace(/([A-Z])/g, " $1").trim();
}

export async function loadSupplierProfile() {
  const user = auth.currentUser;
  if (!user) return null;
  try {
    const snap = await getDoc(doc(db, "suppliers", user.uid));
    if (!snap.exists()) return null;
    return { id: snap.id, ...snap.data() };
  } catch (error) {
    console.warn("Supplier profile is not readable yet:", error?.code || error?.message);
    return null;
  }
}

async function queryRidesByOwnerField(field, value, statuses) {
  if (!value || !statuses?.length) return [];

  const runExactStatus = async (status) => {
    const snap = await getDocs(
      query(
        collection(db, "rides"),
        where(field, "==", value),
        where("status", "==", status)
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  };

  if (statuses.length === 1) return runExactStatus(statuses[0]);

  try {
    const snap = await getDocs(
      query(
        collection(db, "rides"),
        where(field, "==", value),
        where("status", "in", statuses.slice(0, 10))
      )
    );
    return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  } catch (error) {
    const code = String(error?.code || "").toLowerCase();
    const message = String(error?.message || "").toLowerCase();
    if (!code.includes("failed-precondition") && !message.includes("index")) throw error;

    // Avoid making the portal unusable if the multi-status query has no composite
    // index yet. Exact-status equality queries remain supplier-scoped and are
    // merged client-side without broadening Firestore access.
    const groups = await Promise.all(statuses.map(runExactStatus));
    const unique = new Map();
    groups.flat().forEach((ride) => unique.set(ride.id, ride));
    return Array.from(unique.values());
  }
}

export async function loadSupplierRides(statuses) {
  const user = auth.currentUser;
  if (!user) return [];

  try {
    const byUid = await queryRidesByOwnerField("supplierId", user.uid, statuses);
    if (byUid.length > 0 || !user.email) return sortRides(byUid);

    // Legacy fallback only when the supplierId query returns nothing.
    // This remains scoped to the signed-in supplier's verified Auth email.
    const byEmail = await queryRidesByOwnerField("supplierEmail", user.email, statuses);
    return sortRides(byEmail);
  } catch (error) {
    // A query index/permission issue is a data-page error, not an authentication failure.
    console.error("Supplier rides query failed:", error);
    throw error;
  }
}

export async function loadSupplierDrivers() {
  const user = auth.currentUser;
  if (!user) return [];

  const snap = await getDocs(
    query(collection(db, "drivers"), where("supplierId", "==", user.uid))
  );

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => {
      const an = `${a.firstName || ""} ${a.lastName || ""}`.trim().toLowerCase();
      const bn = `${b.firstName || ""} ${b.lastName || ""}`.trim().toLowerCase();
      return an.localeCompare(bn);
    });
}

export async function acceptRide(rideId) {
  return updateDoc(doc(db, "rides", rideId), {
    status: "accepted",
    acceptedAt: serverTimestamp(),
  });
}

export async function rejectRide(rideId) {
  return updateDoc(doc(db, "rides", rideId), {
    status: "adminAccepted",
    supplierEmail: null,
    sharedByAdmin: false,
  });
}

export async function assignRide(rideId, driver) {
  return updateDoc(doc(db, "rides", rideId), {
    status: "assigned",
    driverId: driver.id,
    assignedToDriverId: driver.id,
    driverName: `${driver.firstName || ""} ${driver.lastName || ""}`.trim(),
    driverPhone: driver.phoneNumber || "",
    car: driver.car || {},
  });
}

export async function callDriverFunction(functionName, payload) {
  const user = auth.currentUser;
  if (!user) throw new Error("Authentication required");
  const token = await user.getIdToken();
  const url = `https://europe-west1-clllimousine.cloudfunctions.net/${functionName}`;
  const response = await fetch(url, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(payload || {}),
  });

  let result = {};
  try {
    result = await response.json();
  } catch (_) {
    // Keep a useful fallback below.
  }

  if (!response.ok) {
    throw new Error(result?.error || `Request failed (${response.status})`);
  }
  return result;
}

function sortRides(rides) {
  return [...rides].sort((a, b) => {
    const ad = toDateAny(a.pickupTimestamp)?.getTime() || 0;
    const bd = toDateAny(b.pickupTimestamp)?.getTime() || 0;
    return ad - bd;
  });
}
